/**
 * Modelos de datos para conexiones internas en nodos (Cierres y NAPs)
 * @module types/connections
 * 
 * Este módulo define la estructura para representar cómo las fibras
 * se conectan, sangran y derivan dentro de un cierre o NAP.
 */

import { ColorFibra, ColorBuffer } from './fibers'

// ============================================================================
// TIPOS DE CONEXIÓN INTERNA
// ============================================================================

/**
 * Tipos de conexión que pueden ocurrir dentro de un nodo
 */
export type TipoConexionInterna =
  | 'paso'              // Fibra pasa sin modificación (pass-through)
  | 'fusion'            // Empalme directo fibra a fibra
  | 'sangrado_splitter' // Fibra cortada → entrada de splitter
  | 'salida_splitter'   // Salida de splitter → destino
  | 'muerto'            // Fibra terminada/sin señal (lado cortado)
  | 'reserva'           // Fibra en bucle para uso futuro

// ============================================================================
// ORIGEN DE CONEXIÓN
// ============================================================================

/**
 * Representa el origen de una conexión (de dónde viene la fibra)
 */
export interface OrigenConexion {
  cableId: string
  tubo: number       // Número de buffer/tubo (1-12)
  hilo: number       // Número de fibra dentro del tubo (1-12)
  colorTubo: ColorBuffer
  colorHilo: ColorFibra
}

// ============================================================================
// DESTINOS DE CONEXIÓN
// ============================================================================

/**
 * Destino: Otra fibra de cable (para paso o fusión)
 */
export interface DestinoCable {
  tipo: 'cable'
  cableId: string
  tubo: number
  hilo: number
  colorTubo?: ColorBuffer
  colorHilo?: ColorFibra
}

/**
 * Destino: Entrada de un splitter (para sangrado)
 */
export interface DestinoSplitterEntrada {
  tipo: 'splitter_entrada'
  splitterId: string
}

/**
 * Destino: Salida de un splitter (pigtail hacia otro destino)
 */
export interface DestinoSplitterSalida {
  tipo: 'splitter_salida'
  splitterId: string
  puerto: number
  colorPigtail?: ColorFibra
}

/**
 * Destino: Puerto físico de NAP (adaptador SC/APC)
 */
export interface DestinoPuertoNAP {
  tipo: 'puerto_nap'
  numeroPuerto: number
  tipoConector?: 'SC/APC' | 'SC/UPC' | 'LC'
}

/**
 * Destino: Fibra muerta (sin señal)
 */
export interface DestinoMuerto {
  tipo: 'muerto'
  motivo?: string // Ej: "Lado hacia cliente después de sangrado"
  direccion?: 'hacia_olt' | 'hacia_cliente'
}

/**
 * Destino: Reserva (bucle para uso futuro)
 */
export interface DestinoReserva {
  tipo: 'reserva'
  longitudReserva?: number // metros de fibra en bucle
  ubicacion?: string       // Ej: "Bandeja 2, posición 5"
}

/**
 * Unión de todos los tipos de destino posibles
 */
export type DestinoConexion =
  | DestinoCable
  | DestinoSplitterEntrada
  | DestinoSplitterSalida
  | DestinoPuertoNAP
  | DestinoMuerto
  | DestinoReserva

// ============================================================================
// CONEXIÓN INTERNA COMPLETA
// ============================================================================

/**
 * Conexión interna dentro de un nodo
 * Representa una conexión física entre origen y destino
 */
export interface ConexionInterna {
  id: string
  nodoId: string              // ID del Cierre o NAP donde ocurre
  tipo: TipoConexionInterna
  
  // Origen de la conexión
  origen: OrigenConexion
  
  // Destino de la conexión
  destino: DestinoConexion
  
  // Ubicación física dentro del nodo
  bandeja?: number
  posicion?: number
  ranura?: number
  
  // Mediciones
  atenuacion?: number // dB de la conexión
  
  // Metadatos
  fechaCreacion: Date
  tecnico?: string
  notas?: string
}

// ============================================================================
// CONEXIONES DE NODO COMPLETAS
// ============================================================================

/**
 * Resumen de todas las conexiones dentro de un nodo
 */
export interface ConexionesNodo {
  nodoId: string
  tipoNodo: 'CIERRE' | 'NAP'
  
  // Todas las conexiones internas
  conexiones: ConexionInterna[]
  
  // Splitters instalados con sus mapeos
  splitters: string[] // IDs de SplitterCompleto
  
  // Resumen estadístico
  estadisticas: {
    totalFibrasEntrantes: number
    fibrasEnPaso: number
    fibrasFusionadas: number
    fibrasDerivadas: number
    fibrasMuertas: number
    fibrasReserva: number
    puertosNAPOcupados?: number
    puertosNAPLibres?: number
  }
}

// ============================================================================
// VALIDACIÓN DE CONEXIONES
// ============================================================================

/**
 * Resultado de validación de conexiones
 */
export interface ValidacionConexiones {
  valido: boolean
  errores: ErrorConexion[]
  advertencias: AdvertenciaConexion[]
}

export interface ErrorConexion {
  tipo: 'fibra_huerfana' | 'splitter_sin_entrada' | 'puerto_duplicado' | 'ciclo_detectado'
  mensaje: string
  elementoId?: string
}

export interface AdvertenciaConexion {
  tipo: 'fibra_sin_destino' | 'atenuacion_alta' | 'reserva_excesiva'
  mensaje: string
  elementoId?: string
}

// ============================================================================
// UTILIDADES
// ============================================================================

/**
 * Genera un ID único para una conexión interna
 */
export function generarIdConexion(nodoId: string, origen: OrigenConexion): string {
  return `CON-${nodoId}-${origen.cableId}-T${origen.tubo}-H${origen.hilo}`
}

/**
 * Formatea la identificación de una fibra de forma legible
 */
export function formatearIdentificacionFibra(origen: OrigenConexion): string {
  return `Cable ${origen.cableId} | Tubo ${origen.tubo} (${origen.colorTubo}) | Hilo ${origen.hilo} (${origen.colorHilo})`
}

/**
 * Obtiene el color según posición (TIA-598)
 */
export function getColorPorPosicion(posicion: number): ColorFibra {
  const colores: ColorFibra[] = [
    'azul', 'naranja', 'verde', 'marron', 'gris', 'blanco',
    'rojo', 'negro', 'amarillo', 'violeta', 'rosa', 'turquesa'
  ]
  return colores[(posicion - 1) % 12]
}

/**
 * Obtiene la posición según color (TIA-598)
 */
export function getPosicionPorColor(color: ColorFibra): number {
  const colores: ColorFibra[] = [
    'azul', 'naranja', 'verde', 'marron', 'gris', 'blanco',
    'rojo', 'negro', 'amarillo', 'violeta', 'rosa', 'turquesa'
  ]
  return colores.indexOf(color) + 1
}
